// @hssna/ui-kit/common/Input — 通用输入框
// 规格: SPEC §6 / ADR-0012 (Tailwind) / OLD-01 (适老化)
//
// 支持:
//   - label / error / hint 三段式布局
//   - 适老化样式 (.theme-elderly 下大字号 + 高对比度边框 + 48px 最小高度)
//   - forwardRef (便于 React Hook Form register)
//   - 原生 input 属性透传

import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** 标签 */
  readonly label?: string;
  /** 错误信息 (传入则显示红色边框 + 错误文本) */
  readonly error?: string;
  /** 提示信息 (无错误时显示) */
  readonly hint?: string;
  /** 是否必填 (在 label 后显示 *) */
  readonly required?: boolean;
  /** 右侧附加内容 (如显示密码按钮) */
  readonly trailing?: ReactNode;
  /** 左侧附加内容 (如国家区号) */
  readonly leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    hint,
    required = false,
    trailing,
    leading,
    id,
    className = '',
    disabled,
    ...rest
  },
  ref,
) {
  const inputId = id ?? rest.name;
  const errorId = inputId ? `${inputId}-error` : undefined;
  const hintId = inputId ? `${inputId}-hint` : undefined;
  const describedById = error ? errorId : hint ? hintId : undefined;

  const inputClasses = [
    'w-full rounded-md border bg-white px-3 py-2 text-base text-gray-900',
    'placeholder:text-gray-400 transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-0',
    'disabled:bg-gray-100 disabled:cursor-not-allowed',
    error
      ? 'border-alert-red focus:border-alert-red focus:ring-alert-red/30'
      : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/30',
    // 适老化最小点击区
    'min-h-[44px] theme-elderly:min-h-[48px] theme-elderly:text-elderly theme-elderly:border-2',
    leading ? 'pl-10' : '',
    trailing ? 'pr-10' : '',
    className,
  ].join(' ');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1 block text-sm font-medium text-gray-700 theme-elderly:text-elderly theme-elderly:font-semibold"
        >
          {label}
          {required && <span className="ml-0.5 text-alert-red">*</span>}
        </label>
      )}
      <div className="relative">
        {leading && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            {leading}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          className={inputClasses}
          {...rest}
        />
        {trailing && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">{trailing}</div>
        )}
      </div>
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-sm text-alert-red theme-elderly:text-base"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1 text-sm text-gray-500 theme-elderly:text-base">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
